import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, Navigate } from 'react-router-dom';

import { ButtonSpinner } from '../components/ui/Loader';
import { useLoginMutation, useVerifyLoginOtpMutation, useResendLoginOtpMutation } from '../store/api/authApi';
import { getPostLoginPath, IS_TENANT_MODE, shouldRedirectTenantToDashboard, TENANT_ADMIN_HOME } from '../config/tenantMode';
import { isAuthenticated, storeAuthData } from '../utils/tokenManager';

interface FormErrors {
  email?: string;
  password?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [login, { isLoading, error }] = useLoginMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyLoginOtpMutation();
  const [resendOtp, { isLoading: isResendingOtp }] = useResendLoginOtpMutation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });

  // 2FA state
  const [otpMode, setOtpMode] = useState(false);
  const [tempUserId, setTempUserId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccess, setOtpSuccess] = useState<string | null>(null);

  if (IS_TENANT_MODE && shouldRedirectTenantToDashboard()) {
    return <Navigate to={TENANT_ADMIN_HOME} replace />;
  }

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return t('validation.email.required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('validation.email.invalid');
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('validation.password.required');
    }
    if (password.length < 8) {
      return t('validation.password.minLength');
    }
    if (password.length > 128) {
      return t('validation.password.maxLength');
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    
    // Mark all fields as touched
    setTouched({ email: true, password: true });
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const result = await login(formData).unwrap();
      
      // 2FA required — switch to OTP mode
      if (result.status === 'success' && 'requiresOTP' in result.data && result.data.requiresOTP) {
        setTempUserId(result.data.tempUserId);
        // Mask email: show first 2 chars + domain
        const [local, domain] = result.data.email.split('@');
        setMaskedEmail(`${local.slice(0, 2)}***@${domain}`);
        setOtpMode(true);
        setLocalError(null);
        return;
      }

      if (result.status === 'success' && 'user' in result.data) {
        // Store user data (token is in HttpOnly cookie)
        storeAuthData('', result.data.user);
        
        // Redirect to dashboard or admin panel
        navigate(getPostLoginPath());
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setLocalError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name as keyof FormErrors];
        return newErrors;
      });
    }
    
    // Validate field in real-time if touched
    if (touched[name as keyof typeof touched]) {
      if (name === 'email') {
        const error = validateEmail(value);
        setErrors((prev) => ({
          ...prev,
          email: error,
        }));
      } else if (name === 'password') {
        const error = validatePassword(value);
        setErrors((prev) => ({
          ...prev,
          password: error,
        }));
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    
    // Validate on blur
    if (name === 'email') {
      const error = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        email: error,
      }));
    } else if (name === 'password') {
      const error = validatePassword(value);
      setErrors((prev) => ({
        ...prev,
        password: error,
      }));
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    setOtpSuccess(null);

    if (!otp.trim() || otp.trim().length < 4) {
      setOtpError(t('auth.login.otpInvalid'));
      return;
    }

    try {
      const result = await verifyOtp({ tempUserId, otp: otp.trim() }).unwrap();
      
      if (result.status === 'success' && 'user' in result.data) {
        storeAuthData('', result.data.user);
        navigate(getPostLoginPath());
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setOtpError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleResendOtp = async () => {
    setOtpError(null);
    setOtpSuccess(null);
    try {
      await resendOtp({ tempUserId }).unwrap();
      setOtpSuccess(t('auth.login.otpResent'));
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setOtpError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleBackToLogin = () => {
    setOtpMode(false);
    setTempUserId('');
    setMaskedEmail('');
    setOtp('');
    setOtpError(null);
    setOtpSuccess(null);
    setLocalError(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="w-full max-w-md animate-scale-in relative z-10">
        {/* Login Card with Brand Colors */}
        <div className="card-glass rounded-3xl shadow-2xl p-8 space-y-6">
          {otpMode ? (
            /* OTP Verification Mode */
            <>
              {/* Header */}
              <div className="text-center space-y-2 animate-slide-down">
                <h1 className="text-4xl font-heading font-bold text-brand-accent">
                  {t('auth.login.otpTitle')}
                </h1>
                <p className="text-neutral-medium font-body">
                  {t('auth.login.otpSentTo')} <span className="text-neutral-light font-medium">{maskedEmail}</span>
                </p>
              </div>

              {/* Error Message */}
              {otpError && (
                <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
                  <p className="text-red-300 text-sm font-body">{otpError}</p>
                </div>
              )}

              {/* Success Message */}
              {otpSuccess && (
                <div className="p-4 bg-success/20 border border-success rounded-lg animate-slide-down">
                  <p className="text-green-300 text-sm font-body">{otpSuccess}</p>
                </div>
              )}

              {/* OTP Form */}
              <form onSubmit={(e) => { void handleOtpSubmit(e); }} noValidate className="space-y-5">
                {/* OTP Input */}
                <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                  <label
                    htmlFor="otp"
                    className="block text-sm font-body font-medium text-neutral-light"
                  >
                    {t('auth.login.otpCode')}
                  </label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/[^0-9]/g, ''));
                      setOtpError(null);
                    }}
                    className="w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body text-center text-2xl tracking-[0.5em] border-neutral-dark focus:border-brand-accent"
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                  />
                </div>

                {/* Verify Button */}
                <button
                  type="submit"
                  disabled={isVerifyingOtp}
                  className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-slide-up flex items-center justify-center gap-2"
                  style={{ animationDelay: '0.2s' }}
                >
                  {isVerifyingOtp ? (
                    <>
                      <ButtonSpinner />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('auth.login.otpVerify')
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="text-center space-y-2 animate-slide-up" style={{ animationDelay: '0.3s' }}>
                <button
                  onClick={() => { void handleResendOtp(); }}
                  disabled={isResendingOtp}
                  className="text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body disabled:opacity-50"
                >
                  {isResendingOtp ? t('common.loading') : t('auth.login.otpResend')}
                </button>
                <p className="text-neutral-medium text-sm font-body">
                  <button
                    onClick={handleBackToLogin}
                    className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-medium"
                  >
                    {t('auth.login.backToLogin')}
                  </button>
                </p>
              </div>
            </>
          ) : (
            /* Normal Login Mode */
            <>
              {/* Header */}
              <div className="text-center space-y-2 animate-slide-down">
                <h1 className="text-4xl font-heading font-bold text-brand-accent">
                  {t('auth.login.title')}
                </h1>
                <p className="text-neutral-medium font-body">
                  {t('auth.login.welcomeBack')}
                </p>
              </div>

              {/* Error Message */}
              {(error || localError) && (
                <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down">
                  <p className="text-red-300 text-sm font-body">
                    {localError ||
                      (error && 'data' in error
                        ? (error.data as { message?: string })?.message
                        : t('errors.network'))}
                  </p>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className="space-y-5">
                {/* Email Input */}
                <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                  <label
                    htmlFor="email"
                    className="block text-sm font-body font-medium text-neutral-light"
                  >
                    {t('auth.login.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                      errors.email && touched.email
                        ? 'border-danger focus:ring-danger focus:border-danger'
                        : 'border-neutral-dark focus:border-brand-accent'
                    }`}
                    placeholder="user@example.com"
                  />
                  {errors.email && touched.email && (
                    <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Password Input */}
                <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                  <label
                    htmlFor="password"
                    className="block text-sm font-body font-medium text-neutral-light"
                  >
                    {t('auth.login.password')}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body ${
                      errors.password && touched.password
                        ? 'border-danger focus:ring-danger focus:border-danger'
                        : 'border-neutral-dark focus:border-brand-accent'
                    }`}
                    placeholder="••••••••"
                  />
                  {errors.password && touched.password && (
                    <p className="text-danger text-xs font-body mt-1 animate-slide-down">
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-brand-accent/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-slide-up flex items-center justify-center gap-2"
                  style={{ animationDelay: '0.3s' }}
                >
                  {isLoading ? (
                    <>
                      <ButtonSpinner />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('auth.login.submit')
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="text-center space-y-2 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                {!IS_TENANT_MODE && (
                  <>
                <Link
                  to="/forgot-password"
                  className="block text-sm text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-body"
                >
                  {t('auth.login.forgotPassword')}
                </Link>
                <p className="text-neutral-medium text-sm font-body">
                  {t('auth.login.noAccount')}{' '}
                  <Link
                    to="/register"
                    className="text-brand-accent hover:text-brand-accent/80 transition-colors duration-200 font-medium"
                  >
                    {t('auth.login.registerLink')}
                  </Link>
                </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

